// @flow
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button, Container, Divider, Form, Grid, Icon } from "semantic-ui-react";
import { Field, type FormProps, getFormValues, reduxForm } from "redux-form";
import { Link } from "react-router-dom";
import { shallowEqual, useSelector } from "react-redux";

import { FormLabelWithDivider as FormLabel } from "Commons/Components/FormComponents/components/FormLabelWithDivider";
import {
  DateSelectField,
  ErrorMessageField,
  InputField,
  ProvidersField,
  TariffsField,
} from "modules/ChangeService/ChangeServices/submodules/Old_Edit/components/Fields";
import { IconOptions } from "../../../misc/components/IconOptions";
import { getStepRoute } from "modules/ChangeService/Wizard/misc/Routes";
import {
  CANCELLED_ALREADY,
  CUSTOM_SWITCHING_DATE,
  NEXT_POSSIBLE_DATE,
  SWITCH_PROVIDER,
  NONE,
} from "modules/ChangeService/Wizard/misc/Constants";
import { COMPANY } from "Commons/Utilities/Constants/PropertyTypes";
import { Loading } from "Commons/Components";
import { hasMin3Digits, isFutureDate, isMaxLength } from "Commons/Utilities/validators";
import { getDateParts } from "Commons/Utilities";
import { PROVIDER_IS_NOT_IN_LIST } from "Commons/Modules/Provider/misc/Constants";
import type { CategoryT } from "Commons/Modules/category";
import type { PathT, StepT } from "../../../misc/Types";
import { CommentBox } from "../../../misc/Components";
import { AUTOMATIC_CANCELLATION } from "../../../../misc/Constants";
import { getPreviousNextStep } from "../../../misc/PathHelpers";
import {
  areNotEqual,
  isRequired,
  uniqueCounterNumber,
} from "modules/ChangeService/ChangeServices/submodules/Old_Edit/misc/Validators";
import RadioButtonOptions from "../../../misc/components/RadioButtonOptions";
import FoldableText from "../../../misc/components/FoldableText/FoldableText";
import Styles from "./Provider.module.scss";

type Props = {|
  onNavigation: Function,
  steps: PathT,
  step: StepT,
  authenticated: boolean,
  categorySlug: string,
  category: CategoryT,
|} & FormProps;

function ProviderStep(props: Props) {
  const {
    onNavigation,
    steps,
    step,
    authenticated,
    categorySlug,
    category,
    change,
    initialValues,
    changeServices,
    location,
  } = props;
  const { t } = useTranslation("changeService");

  const { previousStep, nextStep } = getPreviousNextStep(steps, step.id);

  const categoryName = categorySlug === "gas" ? "Gas" : "Strom";

  const currentValues = useSelector((state) => getFormValues("ChangeService")(state), shallowEqual);

  const memoizedDateRequired = useMemo(() => (value) => isFutureDate()(getDateParts(value)), []);

  const counterNumberField =
    currentValues.switchProviderOrMove === SWITCH_PROVIDER ? "counterNumber" : "futureCounterNumber";

  const dateFieldName =
    currentValues.switchProviderOrMove === SWITCH_PROVIDER
      ? currentValues.cancelOldContract === CUSTOM_SWITCHING_DATE
        ? "desiredProviderChangeDate"
        : "canceledMyselfDate"
      : "startOfDeliveryDate";

  const isCompany = currentValues.propertyType === COMPANY;

  const memoizedUniqueCounterNumber = useMemo(
    () => (value, allValues) => uniqueCounterNumber(changeServices[category.id], value, allValues),
    [changeServices, category]
  );

  if (!category) return <Loading />;

  const showSpecialTerminations =
    currentValues.switchProviderOrMove === "SWITCH_PROVIDER" && currentValues.switchTariffDateType;

  return (
    <Form>
      <Grid stackable>
        <Grid.Column>
          {currentValues.switchProviderOrMove === SWITCH_PROVIDER && (
            <>
              <Form.Group>
                <FormLabel
                  width={16}
                  title={t("wizard.provider.currentProvider.label")}
                  centered
                  inline
                  tooltip={t("wizard.provider.currentProvider.info")}
                  secondary
                  className={Styles.questionProvider}
                />
                <FormLabel width={16} title={t("mobile.offer.provider", { categoryName })} />
                <Field
                  name="categoryProviderId"
                  component={ProvidersField}
                  props={{
                    initialValues: {
                      postalcode: currentValues.postalcode,
                      locationId: currentValues.locationId,
                      currentConsumption: currentValues.currentConsumption,
                    },
                    category: {
                      categoryId: category.id,
                    },
                    showExtraLink: false,
                    mobileChangeService: true,
                    placeholder: t("mobile.provider.currentProvider.placeholder"),
                    onChange: (provider) => {
                      if (provider.id !== PROVIDER_IS_NOT_IN_LIST) {
                        change("externalId", provider.company.externalId);
                        change("currentProviderName", provider.company.companyName);
                      }

                      if (initialValues.categoryProviderId !== provider.id) {
                        change("currentTariff", null);
                        change("currentTariffName", null);
                        change("permanentTariffId", null);
                      }

                      if (provider.id === PROVIDER_IS_NOT_IN_LIST) {
                        change("currentProviderName", null);
                      }
                    },
                  }}
                />
                {currentValues.categoryProviderId === PROVIDER_IS_NOT_IN_LIST && (
                  <CommentBox fieldName={"comment"} validation={isRequired} />
                )}
              </Form.Group>
              {currentValues.categoryProviderId !== PROVIDER_IS_NOT_IN_LIST && (
                <Form.Group>
                  <FormLabel width={16} title={t("mobile.offer.tariff", { categoryName })} />
                  <Field
                    name="currentTariff"
                    component={TariffsField}
                    props={{
                      width: 16,
                      categoryType: category.categoryType,
                      categoryProviderId: currentValues.categoryProviderId,
                      postalCode: currentValues.postalcode,
                      mobileChangeService: true,
                      onChange: (tariff) => {
                        change("currentTariffName", tariff.name);
                        change("permanentTariffId", tariff.permanentId);
                      },
                      isCompany,
                    }}
                  />
                </Form.Group>
              )}
            </>
          )}
          <Form.Group>
            <FormLabel
              width={16}
              title={t("wizard.provider.customerDataPresent.label")}
              centered
              inline
              tooltip={t("wizard.provider.customerDataPresent.info")}
              secondary
            />
            <div style={{ margin: "0 auto" }}>
              <RadioButtonOptions
                ariaLabelledby="customerDataLabel"
                name="hasCustomerData"
                normalize={(value) => value === "yes"}
                format={(value) => (value ? "yes" : "no")}
              />
            </div>
          </Form.Group>
          {currentValues.hasCustomerData && (
            <>
              {currentValues.switchProviderOrMove === SWITCH_PROVIDER && (
                <Form.Group>
                  <FormLabel width={16} title={t("mobile.provider.customerNumber", { categoryName })} />
                  <Field
                    name="customerNumber"
                    validate={[isRequired, isMaxLength(30), hasMin3Digits("customer")]}
                    component={InputField}
                    props={{ width: 16 }}
                  />
                </Form.Group>
              )}
              <Form.Group>
                <FormLabel
                  width={16}
                  title={t(`mobile.provider.counterNumber.${currentValues.switchProviderOrMove}`, { categoryName })}
                />
                <Field
                  name={counterNumberField}
                  component={InputField}
                  validate={[
                    isRequired,
                    memoizedUniqueCounterNumber,
                    areNotEqual(
                      currentValues.counterNumber,
                      currentValues.customerNumber,
                      t("validation:notSameCounterCustomerNumber")
                    ),
                    isMaxLength(30),
                    hasMin3Digits("counter"),
                  ]}
                  props={{ width: 16 }}
                />
              </Form.Group>
              {currentValues.switchProviderOrMove === SWITCH_PROVIDER && (
                <Form.Group>
                  <FormLabel
                    width={16}
                    title={t("wizard.provider.cancelOldContract.label")}
                    centered
                    inline
                    tooltip={t("wizard.provider.cancelOldContract.info")}
                    secondary
                  />
                  {showSpecialTerminations && (
                    <div className={Styles.cancelationDate}>
                      <span>{t("wizard.provider.cancelOldContract.note")}</span>
                      <FoldableText
                        className={Styles.foldableText}
                        title={t("wizard.provider.cancelOldContract.specialCancelation.title")}
                        content={t("wizard.provider.cancelOldContract.specialCancelation.body")}
                      />
                    </div>
                  )}
                  <Field
                    name="cancelOldContract"
                    component={IconOptions}
                    props={{
                      options: [
                        {
                          icon: "signup",
                          label: t("wizard.provider.cancelOldContract.options.nextPossibleDate"),
                          value: NEXT_POSSIBLE_DATE,
                          dataCy: NEXT_POSSIBLE_DATE,
                        },
                        {
                          icon: "signup",
                          label: t("wizard.provider.cancelOldContract.options.YES"),
                          value: CUSTOM_SWITCHING_DATE,
                          dataCy: CUSTOM_SWITCHING_DATE,
                        },
                        {
                          icon: "signed",
                          label: t("wizard.provider.cancelOldContract.options.NO"),
                          value: CANCELLED_ALREADY,
                          dataCy: CANCELLED_ALREADY,
                        },
                      ],
                      onChange: (value) => {
                        if (value === CUSTOM_SWITCHING_DATE) {
                          change("switchTariffDateType", CUSTOM_SWITCHING_DATE);
                          change("canceledMyselfDate", null);
                          change("cancellationContractType", AUTOMATIC_CANCELLATION);
                        } else if (value === NEXT_POSSIBLE_DATE) {
                          change("canceledMyselfDate", null);
                          change("cancellationContractType", AUTOMATIC_CANCELLATION);
                          change("desiredProviderChangeDate", null);
                          change("switchTariffDateType", NEXT_POSSIBLE_DATE);
                        } else {
                          change("canceledMyselfDate", null);
                          change("cancellationContractType", CANCELLED_ALREADY);
                          change("switchTariffDateType", NONE);
                        }
                      },
                    }}
                  />
                </Form.Group>
              )}
              {currentValues.cancelOldContract !== NEXT_POSSIBLE_DATE && (
                <>
                  <Form.Group>
                    <FormLabel
                      width={16}
                      title={
                        !(currentValues.switchProviderOrMove === SWITCH_PROVIDER)
                          ? t("wizard.provider.contractStartDate")
                          : currentValues.cancelOldContract === "CANCELLED_ALREADY"
                          ? t("wizard.contract.contractEnd")
                          : null
                      }
                    />
                    <Field
                      name={dateFieldName}
                      validate={[memoizedDateRequired]}
                      data-cy={"desiredProviderChangeDate"}
                      component={DateSelectField}
                      props={{
                        mobileChangeService: true,
                        width: 16,
                        numYears: 5,
                        startYear: new Date().getFullYear(),
                        reverseYears: true,
                      }}
                    />
                  </Form.Group>
                  <Field name={dateFieldName} validate={memoizedDateRequired} component={ErrorMessageField} />
                </>
              )}
            </>
          )}
          <Divider hidden />
          <Container textAlign="center">
            <Link
              to={{
                pathname: getStepRoute(authenticated, categorySlug, nextStep.route),
                search: location.search,
              }}
            >
              <Button primary data-cy="NextStep" size="big" onClick={onNavigation}>
                {t("common:next")}
                <Icon name="arrow right" />
              </Button>
            </Link>
            <Divider hidden />
            <p>
              <Link
                to={{
                  pathname: getStepRoute(authenticated, categorySlug, previousStep.route),
                  search: location.search,
                }}
              >{`< ${t("common:back")}`}</Link>
            </p>
          </Container>
        </Grid.Column>
      </Grid>
    </Form>
  );
}

const Provider = reduxForm({
  form: "ChangeService",
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
})(ProviderStep);

export { Provider };
